import type { TFunction } from 'i18next'
import type { DashboardBook, DashboardUserProfile } from '@services/firebase'
import type {
  LearningAnswerOption,
  LearningDisplayTextValue,
  LearningLocalizedTextMap,
  LearningLesson,
  LearningQuestionItem,
  LearningTranslationEntry,
  LearningWorkbookChapter,
  LearningWorkbookQuizItem,
} from '@services/firebase/learning'

const LESSON_PROGRESS_STORAGE_KEY = 'progress_lessons'
const REMOTE_COMPLETION_PROGRESS_PERCENT = 100
const LOCAL_LESSON_PROGRESS_MAX_BEFORE_COMPLETION = 99
const SELF_STUDY_PARTIAL_PROGRESS_STEP_PERCENT = 20
const SELF_STUDY_PARTIAL_PROGRESS_MAX_PERCENT = 90

interface LocalLessonProgressEntry {
  readonly id: string
  readonly progress: number
}

type RemoteLessonProgressEntry = NonNullable<
  DashboardUserProfile['progress_lessons']
>[number]

type RemoteWorkbookProgressEntry = NonNullable<
  DashboardUserProfile['progress_workbook_lessons']
>[number]

interface SelfStudyProgressEntry {
  readonly chapterId?: string
  readonly selftStudyId?: string
}

export function normalizeLearningLocale(locale: string | undefined) {
  if (!locale) {
    return 'en'
  }

  if (locale === 'vi-VN') {
    return 'vi'
  }

  if (locale === 'en-US') {
    return 'en'
  }

  if (locale.toLowerCase().split('-')[0] === 'es') {
    return 'es'
  }

  if (locale === 'cn' || locale === 'zh-CN' || locale.startsWith('zh-')) {
    return 'zh'
  }

  return locale.includes('-') ? locale.split('-')[0] : locale
}

export function getLearningLocaleFallbacks(locale: string | undefined) {
  const normalizedLocale = normalizeLearningLocale(locale)
  const legacyChineseLocale = normalizedLocale === 'zh' ? 'cn' : undefined

  // Spanish support text may use English, then the Vietnamese source.
  // Preserve the established fallback contract for existing display locales.
  if (normalizedLocale === 'es') {
    return ['es', 'en', 'vi']
  }

  return [normalizedLocale, legacyChineseLocale, 'en'].filter(
    (value, index, values): value is string => Boolean(value) && values.indexOf(value) === index,
  )
}

function isLocalizedDisplayTextMap(
  value: unknown,
): value is LearningLocalizedTextMap {
  return Boolean(value && typeof value === 'object' && !Array.isArray(value))
}

function hasNonEmptyText(value: string | undefined): value is string {
  return typeof value === 'string' && value.trim().length > 0
}

function containsLegacyHtmlMarkup(value: string) {
  return /<[^>]+>|&[a-z0-9#]+;/i.test(value)
}

export function resolveTranslatedText(
  entries: unknown,
  locale: string,
) {
  const fallbacks = getLearningLocaleFallbacks(locale)

  if (typeof entries === 'string') {
    return entries.trim() || undefined
  }

  if (entries && typeof entries === 'object' && !Array.isArray(entries)) {
    const values = entries as Record<string, unknown>
    if (normalizeLearningLocale(locale) !== 'es' && typeof values.text === 'string' && values.text.trim()) {
      return values.text
    }
    for (const code of fallbacks) {
      const value = values[code]
      if (typeof value === 'string' && value.trim()) return value
      if (value && typeof value === 'object' && 'text' in value
        && typeof value.text === 'string' && value.text.trim()) return value.text
    }
    return typeof values.text === 'string' && values.text.trim() ? values.text : undefined
  }

  if (!Array.isArray(entries)) return undefined
  const translations = entries.filter((entry): entry is LearningTranslationEntry =>
    Boolean(entry && typeof entry === 'object' && !Array.isArray(entry)))

  for (const code of fallbacks) {
    const entry = translations.find((item) => item.code === code && hasNonEmptyText(item.text))

    if (typeof entry?.text === 'string' && entry.text.trim()) {
      return entry.text
    }
  }

  if (normalizeLearningLocale(locale) === 'es') {
    return undefined
  }

  return translations.find((item) => hasNonEmptyText(item.text))?.text
}

export function resolveLocalizedMapText(
  entries: LearningLocalizedTextMap | undefined,
  locale: string,
) {
  if (!entries) {
    return undefined
  }

  const fallbacks = getLearningLocaleFallbacks(locale)

  for (const code of fallbacks) {
    const value = entries[code]

    if (hasNonEmptyText(value)) {
      return value
    }
  }

  return normalizeLearningLocale(locale) === 'es'
    ? undefined
    : Object.values(entries).find(hasNonEmptyText)
}

export function resolveLocalizedDisplayText(
  value: LearningDisplayTextValue | undefined,
  locale: string,
  fallback?: string,
) {
  if (typeof value === 'string' && hasNonEmptyText(value)) {
    return value
  }

  if (isLocalizedDisplayTextMap(value)) {
    return resolveLocalizedMapText(value, locale) ?? fallback
  }

  return fallback
}

export function resolveBookTitle(
  book: DashboardBook | null | undefined,
  locale: string,
  fallback = 'Learning program',
) {
  return resolveLocalizedDisplayText(book?.title, locale, book?.id ?? fallback)
}

export function resolveChapterTitle(
  chapter: LearningWorkbookChapter | null | undefined,
  locale: string,
  fallback = 'Self-study chapter',
) {
  return resolveLocalizedDisplayText(chapter?.title, locale, fallback)
}

export function resolveChapterDescription(
  chapter: LearningWorkbookChapter,
  locale: string,
) {
  const translatedDescription = resolveLocalizedMapText(chapter.translate, locale)

  if (hasNonEmptyText(translatedDescription)) {
    if (!containsLegacyHtmlMarkup(translatedDescription)) {
      return translatedDescription
    }
  }

  const introductionDescription = resolveLocalizedMapText(
    chapter.introduction,
    locale,
  )

  if (hasNonEmptyText(introductionDescription)) {
    if (!containsLegacyHtmlMarkup(introductionDescription)) {
      return introductionDescription
    }
  }

  const resolvedTitle = resolveChapterTitle(chapter, locale, '')

  if (!hasNonEmptyText(resolvedTitle)) {
    return undefined
  }

  const resolvedTitleText = resolvedTitle
  const titleParts = resolvedTitleText.split(':')
  const compactTitle = titleParts.length > 1 ? titleParts.slice(1).join(':').trim() : resolvedTitleText

  return hasNonEmptyText(compactTitle) ? compactTitle : resolvedTitleText
}

export function resolveLessonDescription(
  lesson: LearningLesson,
  locale: string,
  fallback?: string,
) {
  return resolveLocalizedMapText(lesson.translate, locale) ?? fallback
}

export function formatLessonTitle(
  t: TFunction<'book'>,
  lesson: LearningLesson,
  locale: string,
  fallbackIndex?: number,
) {
  const lessonTitle = resolveLocalizedDisplayText(lesson.title, locale)

  if (lesson.sort === 0) {
    return lessonTitle ? `${t('Lesson opening')}: ${lessonTitle}` : t('Lesson opening')
  }

  if (typeof lesson.sort === 'number') {
    return lessonTitle
      ? `${t('Lesson', { value: lesson.sort })}: ${lessonTitle}`.trim()
      : t('Lesson', { value: lesson.sort })
  }

  if (lessonTitle) {
    return lessonTitle
  }

  if (typeof fallbackIndex === 'number') {
    return t('Lesson', { value: fallbackIndex })
  }

  return 'Lesson'
}

export function getAudiobookLessons(lessons: LearningLesson[]) {
  return lessons.filter((lesson) => hasNonEmptyText(lesson.audio))
}

function resolveQuestionTextValue(
  question: LearningQuestionItem,
  locale: string,
) {
  return resolveLocalizedMapText(question.translate, locale) ?? question.text
}

function resolveListeningQuizQuestionTextValue(
  question: LearningQuestionItem,
  locale: string,
) {
  // Legacy OnlineListeningQuiz `OtherTemplate`/`QuizTemplate` renders
  // `question.text` directly. Translation is a compatible fallback only when
  // migrated payloads omitted the source field.
  return question.text ?? resolveLocalizedMapText(question.translate, locale)
}

export function clampLearningProgressPercent(progress: number) {
  if (!Number.isFinite(progress)) {
    return 0
  }

  return Math.max(0, Math.min(REMOTE_COMPLETION_PROGRESS_PERCENT, Math.round(progress)))
}

function clampLocalLessonProgressPercent(progress: number) {
  return Math.min(
    LOCAL_LESSON_PROGRESS_MAX_BEFORE_COMPLETION,
    clampLearningProgressPercent(progress),
  )
}

function hasLegacyVisibleStatus(value: unknown) {
  if (value === false || value === 0 || value === 'false') {
    return false
  }

  if (value === true || value === 1) {
    return true
  }

  if (typeof value === 'string') {
    return value.trim().length > 0
  }

  return value !== null && value !== undefined
}

export function resolveLearningQuestionText(
  question: LearningQuestionItem,
  locale: string,
  fallback = 'Question unavailable',
) {
  return resolveQuestionTextValue(question, locale) ?? fallback
}

export function resolveLearningQuestionTexts(
  questions: LearningQuestionItem[] | undefined,
  locale: string,
  fallbackPrefix = 'Question',
) {
  return (questions ?? [])
    .filter((question) => question.status !== false)
    .map(
      (question, index) =>
        resolveQuestionTextValue(question, locale) ??
        `${fallbackPrefix} ${index + 1}`,
    )
}

export function resolveListeningQuizQuestionTexts(
  item: LearningWorkbookQuizItem,
  locale: string,
) {
  const authoredQuestions = (item.questions ?? [])
    .filter((question) => hasLegacyVisibleStatus(question.status))
    .map((question) => resolveListeningQuizQuestionTextValue(question, locale))
    .filter((questionText): questionText is string => hasNonEmptyText(questionText))

  if (authoredQuestions.length > 0) {
    return authoredQuestions
  }

  return [
    resolveLocalizedDisplayText(item.question, locale),
    item.text,
    item.data?.text,
  ].filter((questionText): questionText is string => hasNonEmptyText(questionText))
}

export function resolveListeningQuizTitle(
  item: LearningWorkbookQuizItem,
  locale: string,
) {
  return resolveLocalizedDisplayText(item.title, locale)
}

export function normalizeLearningAnswerOptions(
  answers: LearningAnswerOption[] | undefined,
) {
  return (answers ?? []).filter((answer) => answer.status !== false)
}

export function normalizeListeningQuizAnswerOptions(
  answers: LearningAnswerOption[] | undefined,
) {
  return (answers ?? []).filter((answer) => hasLegacyVisibleStatus(answer.status))
}

export function hasScorableLearningAnswer(answer: LearningAnswerOption) {
  return Boolean(answer.correct ?? answer.isRight ?? answer.result)
}

function hasMeaningfulListeningQuizTitle(value: string | undefined): value is string {
  return typeof value === 'string' && /[\p{L}\p{N}]/u.test(
    value.replace(/<[^>]*>/g, '').replace(/&nbsp;/gi, ' '),
  )
}

export function resolveListeningQuizPresentation(
  item: LearningWorkbookQuizItem,
  locale: string,
) {
  const answers = normalizeListeningQuizAnswerOptions(item.answers)
  const questions = resolveListeningQuizQuestionTexts(item, locale)
  const hasContent =
    hasMeaningfulListeningQuizTitle(resolveListeningQuizTitle(item, locale)) ||
    questions.length > 0 ||
    [item.audio, item.image, item.featuredImage, item.youtubeId].some(hasNonEmptyText)
  const kind: 'MULTIPLE_CHOICE' | 'CONTENT' | 'UNAVAILABLE' =
    item.template === 'multiple_choice' && answers.length > 0
      ? 'MULTIPLE_CHOICE'
      : hasContent ? 'CONTENT' : 'UNAVAILABLE'

  return {
    answers,
    questions,
    kind,
    fullwidth: item.fullwidth ?? false,
    oneThird: item.oneThird ?? false,
  }
}

export function resolveListeningQuizPromptTitle(
  item: LearningWorkbookQuizItem,
  locale: string,
  fallbackNumber?: number,
  fallbackTitle = 'Listening prompt',
) {
  const localizedTitle = resolveLocalizedDisplayText(item.title, locale)

  if (hasMeaningfulListeningQuizTitle(localizedTitle)) {
    return localizedTitle
  }

  const firstQuestion = resolveListeningQuizQuestionTexts(item, locale)
    .find(hasMeaningfulListeningQuizTitle)

  if (firstQuestion) {
    return firstQuestion
  }

  if (typeof item.sort === 'number') {
    return `${fallbackTitle} ${item.sort}`
  }

  if (typeof fallbackNumber === 'number') {
    return `${fallbackTitle} ${fallbackNumber}`
  }

  return fallbackTitle
}

export function getScorableListeningQuizItems(
  items: LearningWorkbookQuizItem[],
) {
  return items.filter(
    (item) =>
      item.template === 'multiple_choice' &&
      normalizeListeningQuizAnswerOptions(item.answers).length > 0,
  )
}

function readLocalLessonProgressEntries(): LocalLessonProgressEntry[] {
  if (typeof window === 'undefined') {
    return []
  }

  const rawValue = window.localStorage.getItem(LESSON_PROGRESS_STORAGE_KEY)

  if (!rawValue) {
    return []
  }

  try {
    const parsed = JSON.parse(rawValue) as unknown

    if (!Array.isArray(parsed)) {
      return []
    }

    return parsed
      .map((entry) => {
        if (!entry || typeof entry !== 'object') {
          return null
        }

        const data = entry as Record<string, unknown>
        const id = typeof data.id === 'string' ? data.id : null
        const progress =
          typeof data.progress === 'number' ? data.progress : null

        if (!id || progress === null) {
          return null
        }

        return { id, progress: clampLocalLessonProgressPercent(progress) }
      })
      .filter((entry): entry is LocalLessonProgressEntry => Boolean(entry))
  } catch {
    return []
  }
}

function writeLocalLessonProgressEntries(entries: LocalLessonProgressEntry[]) {
  if (typeof window === 'undefined') {
    return
  }

  window.localStorage.setItem(
    LESSON_PROGRESS_STORAGE_KEY,
    JSON.stringify(entries),
  )
}

export function getLocalLessonProgress(lessonId: string) {
  const entry = readLocalLessonProgressEntries().find((item) => item.id === lessonId)

  return entry?.progress ?? 0
}

export function upsertLocalLessonProgress(lessonId: string, progress: number) {
  const nextProgress = clampLocalLessonProgressPercent(progress)
  const entries = readLocalLessonProgressEntries()
  const existingIndex = entries.findIndex((item) => item.id === lessonId)

  if (existingIndex >= 0) {
    if (nextProgress > entries[existingIndex].progress) {
      entries[existingIndex] = {
        id: lessonId,
        progress: nextProgress,
      }
    }
  } else {
    entries.push({
      id: lessonId,
      progress: nextProgress,
    })
  }

  writeLocalLessonProgressEntries(entries)
}

export function clearLocalLessonProgress(lessonId: string) {
  const entries = readLocalLessonProgressEntries().filter(
    (item) => item.id !== lessonId,
  )

  writeLocalLessonProgressEntries(entries)
}

function getRemoteLessonProgressEntries(
  myUser: DashboardUserProfile | null,
): RemoteLessonProgressEntry[] {
  const entries = myUser?.progress_lessons

  if (!Array.isArray(entries)) {
    return []
  }

  return entries.filter(
    (entry): entry is RemoteLessonProgressEntry =>
      Boolean(
        entry &&
          typeof entry === 'object' &&
          typeof (entry as { lessonId?: unknown }).lessonId === 'string',
      ),
  )
}

function getRemoteWorkbookProgressEntries(
  myUser: DashboardUserProfile | null,
): RemoteWorkbookProgressEntry[] {
  const entries = myUser?.progress_workbook_lessons

  if (!Array.isArray(entries)) {
    return []
  }

  return entries.filter(
    (entry): entry is RemoteWorkbookProgressEntry =>
      Boolean(
        entry &&
          typeof entry === 'object' &&
          typeof (entry as { lessonId?: unknown }).lessonId === 'string',
      ),
  )
}

export function getLessonCompletionProgress(
  lessonId: string,
  myUser: DashboardUserProfile | null,
) {
  const completed = getRemoteLessonProgressEntries(myUser).some(
    (item) => item.lessonId === lessonId,
  )

  if (completed) {
    return REMOTE_COMPLETION_PROGRESS_PERCENT
  }

  return getLocalLessonProgress(lessonId)
}

export function isLessonCompleted(
  lessonId: string,
  myUser: DashboardUserProfile | null,
) {
  return getRemoteLessonProgressEntries(myUser).some(
    (item) => item.lessonId === lessonId,
  )
}

export function resolveReaderLanguage(locale: string) {
  return normalizeLearningLocale(locale)
}

export function getWorkbookQuizEntry(
  lessonId: string,
  bookId: string | undefined,
  myUser: DashboardUserProfile | null,
) {
  return getRemoteWorkbookProgressEntries(myUser).find(
    (item) => item.lessonId === lessonId && item.bookId === bookId,
  )
}

export function getCurrentSelfStudyChapterId(myUser: DashboardUserProfile | null) {
  return myUser?.selfstudy_chapter_learning?.current_chapter
}

export function getSelfStudyProgressEntries(myUser: DashboardUserProfile | null) {
  const personalization = myUser?.personalization as
    | {
        readonly selfLearning?: unknown
      }
    | null
    | undefined

  if (!Array.isArray(personalization?.selfLearning)) {
    return []
  }

  return personalization.selfLearning.filter(
    (entry): entry is SelfStudyProgressEntry =>
      Boolean(entry && typeof entry === 'object'),
  )
}

export function getChapterCompletionProgress(
  chapter: LearningWorkbookChapter,
  myUser: DashboardUserProfile | null,
) {
  const progressEntries = getSelfStudyProgressEntries(myUser).filter(
    (entry) => entry.chapterId === chapter.id,
  )

  if (progressEntries.some((entry) => entry.selftStudyId === 'implementation')) {
    return REMOTE_COMPLETION_PROGRESS_PERCENT
  }

  if (progressEntries.length === 0) {
    return 0
  }

  return Math.min(
    SELF_STUDY_PARTIAL_PROGRESS_MAX_PERCENT,
    progressEntries.length * SELF_STUDY_PARTIAL_PROGRESS_STEP_PERCENT,
  )
}

export function getAnswerOptionLabel(
  option: LearningAnswerOption | string,
  locale: string,
  fallback = 'Option',
) {
  if (typeof option === 'string') {
    return option
  }

  if (normalizeLearningLocale(locale) === 'es') {
    const sourceAnswer = [option.answer, option.text].find(hasNonEmptyText)
    if (sourceAnswer) {
      return sourceAnswer
    }
  }

  return (
    resolveLocalizedMapText(option.translate, locale) ??
    option.text ??
    option.answer ??
    fallback
  )
}
